import styled from "@emotion/styled";
import { Box, Button as MuiButton } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BannerImage } from "../components/banner-image";
import FinanceSummaryCard from "../components/finance/FinanceSummaryCard";
import GigActivityCard from "../components/home/GigActivityCard";
import InnerLayout from "../components/innerLayout/InnerLayout";
import NotificationCard from "../components/message/Notification/NotificationCard";
import NearbyChefs from "../components/NearbyChefs";
import NearbyGigs from "../components/NearbyGigs";
import NoDataUI from "../components/NoDataUI";
import Typography from "../components/Typography";
import BrowseType from "../constants/BrowseType";
import Tabs from "../constants/tabs";
import UserType from "../constants/UserType";
import { Notification } from "../interfaces/Notification";
import { theme } from "../mui-customs/Theme";
import { setActiveTab } from "../redux/createBrowsePageSlice";
import { setTab } from "../redux/createTabSlice";
import { setSelectedNotification } from "../redux/notificationSlice";
import { RootState } from "../redux/store";

const StyledContainer = styled(Box)`
  width: 100%;
  border-radius: 16px;
  padding: 24px;
  border: 2px solid ${theme.whiteBg.WBG3};
`;

const Home = () => {
  const [isBannerImageLoading, setIsBannerImageLoading] = useState(false);
  const isMdSize = useMediaQuery("(min-width:900px)");
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.user.user);
  const notificationData = useSelector(
    (state: RootState) => state.notifications.notifications
  );
  const router = useRouter();
  const hasLoggedInitial = useRef(false);
  const LOG_PREFIX = "[HomePage]";

  useEffect(() => {
    dispatch(setTab(Tabs.Home));
  }, []);

  useEffect(() => {
    const shouldRedirect =
      (!userData.uid || !isMdSize) && router.asPath === "/";
    console.log(router.asPath === "/");

    if (shouldRedirect) {
      console.info(LOG_PREFIX, "redirectingToBrowse", {
        path: router.asPath,
        host:
          typeof window !== "undefined" ? window.location.hostname : undefined,
        uidPresent: Boolean(userData.uid),
        isMdSize,
        reason: !userData.uid
          ? "missing-uid"
          : !isMdSize
          ? "below-md"
          : "unknown",
      });
      console.log("From Index");
      router.replace("/browse");
      return;
    }
    console.info(LOG_PREFIX, "stayingOnHome", {
      path: router.asPath,
      host:
        typeof window !== "undefined" ? window.location.hostname : undefined,
      uidPresent: Boolean(userData.uid),
      isMdSize,
    });
  }, [isMdSize]);

  // Log key state once on initial render and when critical values change
  useEffect(() => {
    // Log once on first client render
    if (!hasLoggedInitial.current) {
      hasLoggedInitial.current = true;
      console.info(LOG_PREFIX, "mount", {
        path: router.asPath,
        host:
          typeof window !== "undefined" ? window.location.hostname : undefined,
        uidPresent: Boolean(userData.uid),
        userType: userData.userType,
        isMdSize,
        notifications: notificationData.length,
      });
      return;
    }
    // Subsequent concise deltas
    console.info(LOG_PREFIX, "stateChange", {
      path: router.asPath,
      host:
        typeof window !== "undefined" ? window.location.hostname : undefined,
      uidPresent: Boolean(userData.uid),
      userType: userData.userType,
      isMdSize,
      notifications: notificationData.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.uid, userData.userType, isMdSize, notificationData.length]);

  const onNotificationClick = (notification: Notification) => {
    router.push("/message");
    dispatch(setSelectedNotification(notification));
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme.whiteBg.WBG1,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: 3,
      }}
    >
      <BannerImage isEasyEditable={true} />
      <FinanceSummaryCard />
      <StyledContainer>
        <Typography variant="h2" sx={{ color: theme.black[800] }}>
          Latest Gig Activity
        </Typography>
        <Box sx={{ mt: 2 }}>
          <GigActivityCard
            iconColor={theme.brand1[300]}
            label={"Accepted/Booked"}
          />
          <GigActivityCard iconColor={theme.grey1[200]} label={"Draft Phase"} />
          <GigActivityCard
            iconColor={theme.pink[200]}
            label={"Awaiting Payment"}
          />
          <GigActivityCard iconColor={theme.purple[200]} label={"Open Bid"} />
          <GigActivityCard
            iconColor={theme.yellow[200]}
            label={"Invoice Change (0)"}
          />
        </Box>
      </StyledContainer>
      <StyledContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h2" sx={{ color: theme.black[800], mt: 2 }}>
            Recent Notifications & Messages
          </Typography>
          <MuiButton
            variant="text"
            sx={{
              textTransform: "none",
              color: theme.blue[400],
              height: "fit-content",
            }}
            onClick={() => {
              router.push("/message");
            }}
          >
            See all
          </MuiButton>
        </Box>
        <StyledContainer sx={{ mt: 4 }}>
          {notificationData.length > 0 ? (
            <>
              {notificationData.slice(0, 5).map((notification) => (
                <NotificationCard
                  key={notification.notificationId}
                  notification={notification}
                  onClick={() => onNotificationClick(notification)}
                />
              ))}
            </>
          ) : (
            <NoDataUI displayMessage="No Notifications found" />
          )}
        </StyledContainer>
      </StyledContainer>
      <StyledContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h2" sx={{ color: theme.black[800], mt: 2 }}>
            {`Highlighted ${
              userData.userType == UserType.Client ? "Chefs" : "Gigs"
            } Near You`}
          </Typography>
          <MuiButton
            variant="text"
            sx={{
              textTransform: "none",
              color: theme.blue[400],
              height: "fit-content",
            }}
            onClick={() => {
              userData.userType === UserType.Client &&
                dispatch(setActiveTab(BrowseType.Chef));
              router.push("/browse");
            }}
          >
            {`Browse ${
              userData.userType == UserType.Client ? "chefs" : "gigs"
            }`}
          </MuiButton>
        </Box>
        {userData.userType === UserType.Client ? (
          <NearbyChefs />
        ) : (
          <NearbyGigs />
        )}
      </StyledContainer>
    </Box>
  );
};

Home.getLayout = function getLayout(component: ReactElement) {
  return <InnerLayout>{component}</InnerLayout>;
};

export default Home;
